import { Button, DialogActions, DialogContent, Stack, TextField } from '@mui/material';
import React from 'react';
import { useDispatch } from 'react-redux';
import { BhiDialog } from 'src/UIKit';
import { useUpdateCampaignPresetApi } from 'src/api/campaignPresets';
import { CampaignPreset } from 'src/api/campaignPresets/types';
import { setCreateOptionPresetInfo } from 'src/modules/campaignCreateOptions/slices';
import { usePresetNameDescriptionForm } from '../../helpers/usePresetNameDescriptionForm';

type Props = {
  open: boolean;
  companyId: string;
  preset: CampaignPreset;
  onClose: () => void;
  onSaved?: (updated: CampaignPreset) => void;
};

// Dialog for renaming a campaign preset / editing its description. Content
// (messages, audience, keywords) is intentionally not editable here.
export const EditPresetDialog: React.FC<Props> = ({ open, companyId, preset, onClose, onSaved }) => {
  const dispatch = useDispatch();
  const updatePreset = useUpdateCampaignPresetApi();

  const { name, setName, description, setDescription, saving, canSave, handleSave } =
    usePresetNameDescriptionForm({
      open,
      initialName: preset.name,
      initialDescription: preset.description,
      seedDeps: [preset.id, preset.name, preset.description],
      logMessage: 'Failed to update campaign preset:',
      toastMessage: 'Failed to update template. Please try again.',
      onSave: async ({ name: trimmedName, description: trimmedDescription }) => {
        const response = await updatePreset(
          preset.id,
          { name: trimmedName, description: trimmedDescription },
          companyId,
        );
        dispatch(
          setCreateOptionPresetInfo({ id: preset.id, name: trimmedName, description: trimmedDescription }),
        );
        onSaved?.(response.data);
        onClose();
      },
    });

  return (
    <BhiDialog open={open} onClose={onClose} title="Edit Custom Template" fullWidth>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <TextField label="Name" value={name} onChange={e => setName(e.target.value)} fullWidth />
          <TextField
            label="Description"
            value={description}
            onChange={e => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave || saving}>
          Save
        </Button>
      </DialogActions>
    </BhiDialog>
  );
};

export default EditPresetDialog;
